"use client";

import { cn } from "../../lib/utils";
import React, { useEffect, useRef, useState } from "react";
import {
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
} from "motion/react";

export function CanvasRevealEffect({
  animationSpeed = 0.4,
  containerClassName,
  colors = [[59, 130, 246]],
  dotSize = 2,
  showGradient = true,
  mouseX,
  mouseY,
}: {
  /**
   * 0.1 - 1 is the recommended range for animation speed.
   */
  animationSpeed?: number;
  containerClassName?: string;
  colors?: number[][];
  dotSize?: number;
  showGradient?: boolean;
  mouseX?: ReturnType<typeof useMotionValue<number>>;
  mouseY?: ReturnType<typeof useMotionValue<number>>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gap = 5;
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [dpr, setDpr] = useState(1);
  const internalMouseX = useMotionValue(0);
  const internalMouseY = useMotionValue(0);
  
  const activeMouseX = mouseX || internalMouseX;
  const activeMouseY = mouseY || internalMouseY;

  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const w = Math.max(Math.floor(rect.width), 100);
        const h = Math.max(Math.floor(rect.height), 100);
        const d = window.devicePixelRatio || 1;
        setDpr(d);
        setCanvasSize({ w, h });
        canvas.width = w * d;
        canvas.height = h * d;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.scale(d, d);
        }
      }
    };

    // Aguardar um frame para garantir que o DOM está pronto
    const timeoutId = setTimeout(updateCanvasSize, 0);
    window.addEventListener('resize', updateCanvasSize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, []);

  function handleMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement>) {
    if (!mouseX || !mouseY) {
      let { left, top } = currentTarget.getBoundingClientRect();
      internalMouseX.set(clientX - left);
      internalMouseY.set(clientY - top);
    }
  }

  const dotsRef = useRef<Array<{ x: number; y: number; color: number[] }>>([]);

  useEffect(() => {
    if (canvasSize.w > 0 && canvasSize.h > 0) {
      const dots: Array<{ x: number; y: number; color: number[] }> = [];
      for (let i = 0; i < canvasSize.w; i = i + gap) {
        for (let j = 0; j < canvasSize.h; j = j + gap) {
          dots.push({
            x: i,
            y: j,
            color: colors[Math.floor(Math.random() * colors.length)],
          });
        }
      }
      dotsRef.current = dots;
    }
  }, [canvasSize, colors]);

  useAnimationFrame(() => {
    if (canvasRef.current && canvasSize.w > 0 && canvasSize.h > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (ctx && dotsRef.current.length > 0) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const mx = activeMouseX.get();
        const my = activeMouseY.get();
        const distanceThreshold = 120;

        for (const dot of dotsRef.current) {
          const dx = dot.x - mx;
          const dy = dot.y - my;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < distanceThreshold) {
            const opacity = 1 - distance / distanceThreshold;
            const size = dotSize * opacity;
            ctx.beginPath();
            ctx.arc(dot.x, dot.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = `rgba(${dot.color[0]}, ${dot.color[1]}, ${dot.color[2]}, ${opacity * 0.8})`;
            ctx.fill();
          }
        }
      }
    }
  });

  return (
    <div
      ref={containerRef}
      className={cn(
        "pointer-events-none absolute inset-0",
        containerClassName
      )}
      onMouseMove={handleMouseMove}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{
          width: `${canvasSize.w}px`,
          height: `${canvasSize.h}px`,
        }}
      />
    </div>
  );
}

